import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { palette } from 'entity/createTheme';
import { useTranslation } from 'react-i18next';
import QuestionContentSelector from './QuestionContentSelector';
import ContentUploader from './ContentUploader';
import {
  UploadVideoMeta,
  UploadImageMeta,
  ExternalVideoURLMeta,
  UploadPDFMeta,
  ContentType,
  ContentMeta,
} from 'types/App';
import { questionContentTypeOptions } from './constants';
import { ContentViewType } from 'entity/Content';

type Props = {
  questionSetContent: ContentViewType;
  contentType: ContentType;
  videoMeta: UploadVideoMeta;
  externalVideoURLMeta: ExternalVideoURLMeta;
  imageMeta: UploadImageMeta;
  pdfMeta: UploadPDFMeta;
  handleChangeContentType: (type: ContentType, resetEditMode?: boolean) => void;
  handleChangeContentMeta: (meta: ContentMeta) => void;
  isQuestionModalOpen: boolean;
  modalRef: React.RefObject<HTMLDivElement>;
  creativeId?: string | null;
  setIsContentTypeChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  isContentTypeChanged?: boolean;
  isSelectedCreativeLoading: boolean;
  setIsSelectedCreativeLoading: React.Dispatch<React.SetStateAction<boolean>>;
  resetExistingContentMeta: () => void;
  editMode?: boolean;
  fileTypeError: string;
  handleFileTypeError: (fileTypeError: string) => void;
};

const UploadContent: FC<Props> = ({
  questionSetContent,
  contentType,
  videoMeta,
  externalVideoURLMeta,
  imageMeta,
  pdfMeta,
  handleChangeContentType,
  handleChangeContentMeta,
  isQuestionModalOpen,
  modalRef,
  creativeId,
  isContentTypeChanged,
  setIsContentTypeChanged,
  isSelectedCreativeLoading,
  setIsSelectedCreativeLoading,
  resetExistingContentMeta,
  editMode = true,
  fileTypeError,
  handleFileTypeError,
}) => {
  const { t } = useTranslation();

  const [isCreateFromExisting, setIsCreateFromExisting] = useState(true);

  const uploadStatus =
    questionSetContent?.content_type === 'videos' ? questionSetContent.upload_status : '';

  const [videoUploadStatus, setVideoUploadStatus] = useState(uploadStatus);
  useEffect(() => {
    setVideoUploadStatus(uploadStatus);
  }, [uploadStatus]);

  const handleChangeMeta = (data: ContentMeta) => {
    handleChangeContentMeta(data);
    setVideoUploadStatus('');
  };

  const selectContentType = (type: ContentType) => {
    const { content_type } = questionSetContent;
    if (['videos', 'images', 'pdfs'].includes(content_type) && type === 'videos_images_pdfs') {
      handleChangeContentType(content_type, true);
    } else {
      handleChangeContentType(type as ContentType, true);
    }
    setIsContentTypeChanged && setIsContentTypeChanged(true);
  };

  return (
    <Root>
      <h4>{t('admin.questions.content')}</h4>
      <ContentSelector
        contentTypeList={questionContentTypeOptions}
        contentType={contentType}
        selectContentType={selectContentType}
        setIsSelectedCreativeLoading={setIsSelectedCreativeLoading}
        setIsCreateFromExisting={setIsCreateFromExisting}
        creativeId={creativeId}
        resetExistingContentMeta={resetExistingContentMeta}
        isQuestionModalOpen={isQuestionModalOpen}
      />
      <ContentUploader
        contentType={contentType}
        videoMeta={videoMeta}
        externalVideoURLMeta={externalVideoURLMeta}
        imageMeta={imageMeta}
        pdfMeta={pdfMeta}
        handleChangeContentMeta={handleChangeMeta}
        uploadStatus={videoUploadStatus}
        handleChangeContentType={handleChangeContentType}
        isQuestionModalOpen={isQuestionModalOpen}
        modalRef={modalRef}
        creativeId={creativeId}
        isContentTypeChanged={isContentTypeChanged || false}
        setIsContentTypeChanged={setIsContentTypeChanged}
        isSelectedCreativeLoading={isSelectedCreativeLoading}
        setIsSelectedCreativeLoading={setIsSelectedCreativeLoading}
        resetExistingContentMeta={resetExistingContentMeta}
        isCreateFromExisting={isCreateFromExisting}
        setIsCreateFromExisting={setIsCreateFromExisting}
        originalContent={questionSetContent}
        editMode={editMode}
        fileTypeError={fileTypeError}
        handleFileTypeError={handleFileTypeError}
      />
    </Root>
  );
};

const Root = styled('div')({
  marginBottom: 20,
  h4: {
    marginBottom: 10,
    fontSize: 16,
    color: palette.darkMedium2,
  },
});
const ContentSelector = styled(QuestionContentSelector)({
  marginBottom: 24,
});

export default UploadContent;
